// AI Recommendations Service
// Generates personalized recommendations using OpenAI (via Supabase Edge Function)
// Falls back to template recommendations if AI is unavailable

import type { MasterResult } from './master-engine';

export interface AIRecommendation {
  title: string;
  description: string;
  actionItems: string[];
  expectedImpact: string;
  timeframe: '1-4 Wochen' | '1-2 Monate' | '2-3 Monate';
}

export interface AIRecommendationsResponse {
  success: boolean;
  recommendations: AIRecommendation[];
  isAIGenerated: boolean;
  metadata?: {
    model?: string;
    timestamp?: string;
    companyName?: string;
  };
}

/**
 * Generate AI-powered recommendations based on audit data
 * Falls back to template recommendations if AI is unavailable
 */
export async function generateAIRecommendations(
  result: MasterResult,
  companyDomain?: string,
  structuredPayload?: unknown
): Promise<AIRecommendationsResponse & { structuredOutput?: unknown }> {
  const { normalizedData, computedMetrics, topLevers, dataGaps } = result;

  // Prepare audit data for AI
  const auditData = {
    companyName: normalizedData.companyName || 'Ihr Unternehmen',
    companyDomain: companyDomain || undefined,
    industry: normalizedData.industry || 'B2B',
    teamSize: normalizedData.teamSize || 5,
    currentRevenue: computedMetrics.currentRevenue || 0,
    potentialRevenue: computedMetrics.potentialRevenue || 0,
    topLevers: topLevers.slice(0, 3).map(lever => ({
      title: lever.title,
      monthlyImpact: lever.monthlyImpact,
      whyItMatters: lever.whyItMatters,
    })),
    dataGaps: (dataGaps || []).slice(0, 5).map(gap => ({
      field: gap.field,
      severity: gap.severity,
    })),
    conversionGaps: {
      contactToDiscovery: {
        current: computedMetrics.convContactToDiscovery || 0,
        benchmark: computedMetrics.benchmarkContactToDiscovery || 0,
      },
      discoveryToPresentation: {
        current: computedMetrics.convDiscoveryToPresentation || 0,
        benchmark: computedMetrics.benchmarkDiscoveryToPresentation || 0,
      },
      presentationToDeal: {
        current: computedMetrics.convPresentationToDeal || 0,
        benchmark: computedMetrics.benchmarkPresentationToDeal || 0,
      },
    },
  };

  try {
    // Try to call Supabase Edge Function
    // NOTE: This requires Supabase client to be initialized
    // If not available, will fall through to catch block
    
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseKey) {
      return generateTemplateRecommendations(result);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 seconds timeout

    const response = await fetch(`${supabaseUrl}/functions/v1/generate_ai_recommendations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseKey}`,
      },
      body: JSON.stringify({
        result,
        companyDomain,
        structuredPayload: structuredPayload || auditData,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error('Edge function error:', response.status);
      return generateTemplateRecommendations(result);
    }

    const data = await response.json();

    if (data.success && data.recommendations && data.recommendations.length > 0) {
      return {
        success: true,
        recommendations: data.recommendations,
        isAIGenerated: true,
        metadata: data.metadata,
        structuredOutput: data.structuredOutput || null,
      };
    } else {
      return generateTemplateRecommendations(result);
    }

  } catch (error) {
    console.error('❌ Error calling AI service:', error);
    return generateTemplateRecommendations(result);
  }
}

/**
 * Generate template-based recommendations (fallback)
 * Uses audit data to customize templates
 */
function generateTemplateRecommendations(result: MasterResult): AIRecommendationsResponse {
  const { normalizedData, computedMetrics, topLevers } = result;

  const recommendations: AIRecommendation[] = [];

  // Recommendation 1: Based on top lever
  const topLever = topLevers[0];
  if (topLever) {
    if (topLever.title.includes('CRM')) {
      recommendations.push({
        title: 'CRM-Fundament aufbauen',
        description: `Ihr CRM ist aktuell nicht optimal genutzt. Mit ${normalizedData.teamSize} Mitarbeitern im Team ist ein sauberes CRM-Setup essentiell für Pipeline-Transparenz und verlässliche Forecasts.`,
        actionItems: [
          'Woche 1: CRM-Audit durchführen - Welche Daten fehlen? Welche Prozesse sind nicht abgebildet?',
          'Woche 2: Pipeline-Stages mit klaren Ein- und Ausstiegskriterien definieren',
          'Woche 3-4: Team-Training und wöchentliches Pipeline-Review etablieren',
        ],
        expectedImpact: `+${Math.round((computedMetrics.benchmarkDiscoveryToPresentation - computedMetrics.convDiscoveryToPresentation) * 10) / 10}% Discovery-to-Presentation Conversion in 6 Wochen`,
        timeframe: '1-2 Monate',
      });
    } else if (topLever.title.includes('Reaktionszeit')) {
      recommendations.push({
        title: 'Lead-Response-Zeit drastisch verkürzen',
        description: `Schnelle Reaktion ist der größte Hebel für mehr Discovery Calls. Aktuell liegt Ihre Kontakt-zu-Discovery Conversion bei ${computedMetrics.convContactToDiscovery.toFixed(1)}%, Benchmark ist ${computedMetrics.benchmarkContactToDiscovery.toFixed(1)}%.`,
        actionItems: [
          'Woche 1: Lead-Routing einrichten - Jeder Lead wird innerhalb von 60 Minuten kontaktiert',
          'Woche 2: Reaktionszeit messen und im Dashboard sichtbar machen',
          'Woche 3-4: Automatische Benachrichtigungen bei neuen Leads aktivieren',
        ],
        expectedImpact: `+${Math.round((computedMetrics.benchmarkContactToDiscovery - computedMetrics.convContactToDiscovery) * 10) / 10}% mehr Discovery Calls in 4 Wochen`,
        timeframe: '1-4 Wochen',
      });
    } else {
      recommendations.push({
        title: 'Follow-up-Disziplin systematisieren',
        description: `Strukturierte Follow-ups sind der Schlüssel zu höheren Abschlussraten. Mit ${normalizedData.teamSize} Reps brauchen Sie klare Prozesse statt Ad-hoc-Aktionen.`,
        actionItems: [
          'Woche 1: Next-Step-Pflicht einführen - Nach jedem Gespräch wird der nächste Termin vereinbart',
          'Woche 2: Follow-up-Sequenzen im CRM automatisieren',
          'Woche 3-4: Wöchentliches Review - Welche Deals haben kein Follow-up?',
        ],
        expectedImpact: `+${Math.round((computedMetrics.benchmarkPresentationToDeal - computedMetrics.convPresentationToDeal) * 10) / 10}% Abschlussrate in 6 Wochen`,
        timeframe: '1-2 Monate',
      });
    }
  }

  // Recommendation 2: Pipeline Transparency
  recommendations.push({
    title: 'Pipeline-Transparenz herstellen',
    description: `Ohne klare Sicht auf die Pipeline können Sie nicht steuern. Etablieren Sie wöchentliche Pipeline-Reviews mit dem gesamten Team.`,
    actionItems: [
      'Woche 1: Festen Termin für wöchentliches Pipeline-Review blocken (30 Minuten)',
      'Woche 2: Agenda-Template erstellen (Welche Deals bewegen sich? Wo hakt es?)',
      'Woche 3-4: Review-Routine etablieren und erste Optimierungen ableiten',
    ],
    expectedImpact: '+20% Pipeline-Bewegung durch bessere Steuerung in 4 Wochen',
    timeframe: '1-4 Wochen',
  });

  // Recommendation 3: Data Quality
  recommendations.push({
    title: 'CRM-Datenqualität systematisch verbessern',
    description: `Schlechte Daten führen zu schlechten Entscheidungen. Ein einmaliges CRM-Audit deckt die größten Lücken auf.`,
    actionItems: [
      'Woche 1: Stichprobe von 20 Deals analysieren - Was fehlt? Was ist veraltet?',
      'Woche 2: Liste mit Top 5 Verbesserungen erstellen und priorisieren',
      'Woche 3-4: Quick Wins umsetzen (Pflichtfelder, Automatisierungen)',
    ],
    expectedImpact: '+30% Datenqualität und bessere Forecast-Genauigkeit in 4 Wochen',
    timeframe: '1-4 Wochen',
  });

  // Recommendation 4: Team Enablement
  recommendations.push({
    title: 'Team-Enablement: Prozesse dokumentieren',
    description: `Mit ${normalizedData.teamSize} Mitarbeitern brauchen Sie dokumentierte Prozesse. Jeder sollte wissen, wie der ideale Sales-Prozess aussieht.`,
    actionItems: [
      'Monat 1: Sales-Playbook erstellen (Discovery-Framework, Einwandbehandlung, Closing)',
      'Monat 2: Team-Workshops durchführen und Best Practices teilen',
      'Monat 3: Onboarding-Prozess für neue Reps etablieren',
    ],
    expectedImpact: '+15% Produktivität durch standardisierte Prozesse in 3 Monaten',
    timeframe: '2-3 Monate',
  });

  return {
    success: true,
    recommendations,
    isAIGenerated: false,
  };
}
